import { abbreviateNumber } from '@src/utils/data';
import { Header } from '@Components/StickyTable/BPMTable';
import { TableProgressBar } from '@Components/TableProgressBar';

interface MaxValues {
  dpvRate: number;
  conversionRate: number;
  ntbRepeatRate: number;
  purchaseRate: number;
}

interface GeoTableRow {
  state: string;
  impressions: number;
  reach: number;
  dpv: number;
  customersConverted: number;
  orders: number;
  revenue: number;
  ntbCustomers: number;
  dpvRate: number;
  conversionRate: number;
  ntbRepeatRate: number;
  ntbPurchaseRate: number;
}

const progressBarRenderer = (value: number, maxCompleted: number) => (
  <TableProgressBar label={`${value}%`} maxCompleted={maxCompleted} value={value} />
);

// Every non-state column renders either an abbreviated number ("81.7m", "22.7k") or a percentage
// progress bar — neither needs anywhere near as much width as a raw number would, so these are
// sized to fit their actual content (plus a bit of room for the header label) rather than the
// uniform 141px carried over from bpm-frontend, which forced the table into horizontal scroll.
export const getGeoTableHeaders = (maxValues: MaxValues): Header[] => [
  { name: 'state', label: 'State', width: 120 },
  {
    name: 'impressions',
    label: 'Impressions',
    flex: 1,
    minFlexWidth: 90,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.impressions)
  },
  {
    name: 'reach',
    label: 'Reach',
    flex: 1,
    minFlexWidth: 90,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.reach)
  },
  {
    name: 'dpv',
    label: 'DPV',
    flex: 1,
    minFlexWidth: 90,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.dpv)
  },
  {
    name: 'customersConverted',
    label: 'Customers Converted',
    flex: 1,
    minFlexWidth: 110,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.customersConverted)
  },
  {
    name: 'orders',
    label: 'Orders',
    flex: 1,
    minFlexWidth: 90,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.orders)
  },
  {
    name: 'revenue',
    label: 'Revenue',
    flex: 1,
    minFlexWidth: 90,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.revenue)
  },
  {
    name: 'ntbCustomers',
    label: 'NTB Customers',
    flex: 1,
    minFlexWidth: 100,
    renderer: (data: GeoTableRow) => abbreviateNumber(data.ntbCustomers)
  },
  {
    name: 'dpvRate',
    label: 'DPVR',
    flex: 1,
    minFlexWidth: 100,
    renderer: (data: GeoTableRow) => progressBarRenderer(data.dpvRate, maxValues.dpvRate)
  },
  {
    name: 'conversionRate',
    label: 'Conversion Rate',
    flex: 1,
    minFlexWidth: 120,
    renderer: (data: GeoTableRow) =>
      progressBarRenderer(data.conversionRate, maxValues.conversionRate)
  },
  {
    name: 'ntbRepeatRate',
    label: 'NTB Repeat Rate',
    flex: 1,
    minFlexWidth: 120,
    renderer: (data: GeoTableRow) =>
      progressBarRenderer(data.ntbRepeatRate, maxValues.ntbRepeatRate)
  },
  {
    name: 'ntbPurchaseRate',
    label: 'NTB Purchase Rate',
    flex: 1,
    minFlexWidth: 120,
    renderer: (data: GeoTableRow) =>
      progressBarRenderer(data.ntbPurchaseRate, maxValues.purchaseRate)
  }
];
